import { AnalyticsPanel } from '@/shared/components/ui/AnalyticsPanel';
import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/routes/paths';
import {
  type DispatchProgress,
  useSalesOrders,
  useSaleReturns,
  useShipments,
  useCommissionAccruals,
  useSalesOrderAction,
  useShipmentAction,
  useReturnAction,
  useCreateSalesOrder,
  useSalesCustomers,
  useCreditNotes,
  useConvertSOtoPO,
  useRepeatOrders,
  useCreateRepeatOrder,
  useRepeatOrderAction,
  useRelatedProducts,
} from '../hooks/useSalesOpsApi';
import { emptyLineFromProduct } from '@/shared/components/commerce/LineItemsEditor';
import { Button } from '@/shared/components/ui/Button';
import { PageHeader } from '@/shared/components/ui/PageHeader';
import { StatCard, StatGrid } from '@/shared/components/ui/StatCard';
import { Input } from '@/shared/components/ui/Input';
import { Modal } from '@/shared/components/ui/Modal';
import { Select } from '@/shared/components/ui/Select';
import { DocumentActions } from '@/shared/components/documents/DocumentActions';
import {
  FileText,
  RotateCcw,
  Truck,
  DollarSign,
  Plus,
  CreditCard,
  Search,
  RefreshCcw,
  ClipboardList,
  TrendingUp,
  Repeat,
  Star,
  IndianRupee,
  PackageCheck,
  Percent,
  Wallet,
  Eye,
  MoreVertical,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { RecordAdvanceModal } from '@/features/sales/components/RecordAdvanceModal';
import { ModuleGuide } from '@/shared/components/ui/ModuleGuide';
import { LineItemsEditor, type DocumentLine } from '@/shared/components/commerce/LineItemsEditor';
import { formatINR, formatDocTotal, docInrEquivalent } from '@/shared/utils/currency';
import { SalesOpsAnalytics } from '../components/SalesOpsAnalytics';
import { localToday } from '@/shared/utils/utils';

function errorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const maybe = err as { response?: { data?: { error?: string } } };
    if (maybe.response?.data?.error) return maybe.response.data.error;
  }
  return fallback;
}

/* ── New sales order / quotation modal ─────────────────────────────────────── */
function NewSalesOrderModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { data: customers = [] } = useSalesCustomers(open);
  const createOrder = useCreateSalesOrder();
  const [documentType, setDocumentType] = useState('sales_order');
  const [customerId, setCustomerId] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('Net 30');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<DocumentLine[]>([]);

  const submit = () => {
    if (!customerId && !customerName.trim()) {
      toast.error('Select a customer or enter a walk-in customer name.');
      return;
    }
    if (lines.length === 0) {
      toast.error('Add at least one product line.');
      return;
    }
    createOrder.mutate(
      {
        documentType,
        customerId: customerId || undefined,
        customerName: customerId ? undefined : customerName.trim(),
        paymentTerms,
        notes,
        lines,
      },
      {
        onSuccess: () => {
          toast.success(documentType === 'quotation' ? 'Quotation created.' : 'Sales order saved as draft.');
          setCustomerId('');
          setCustomerName('');
          setNotes('');
          setLines([]);
          onClose();
        },
        onError: (err) => toast.error(errorMessage(err, 'Could not create the document.')),
      }
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Create Sales Document"
      size="lg"
      footer={
        <div className="flex justify-end gap-2.5">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={createOrder.isPending} className="bg-blue-600 hover:bg-blue-700 font-bold">
            {createOrder.isPending ? 'Saving…' : 'Save Document'}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-3">
          <Select
            label="Document Type"
            name="so-type"
            options={[
              { label: 'Sales Order', value: 'sales_order' },
              { label: 'Quotation', value: 'quotation' },
              { label: 'Proforma Invoice', value: 'proforma' },
            ]}
            value={documentType}
            onChange={(e) => setDocumentType(e.target.value)}
          />
          <Select
            label="Customer / Account"
            name="so-customer"
            options={customers.map((c) => ({ label: c.name, value: c.id }))}
            placeholder={customers.length ? 'Select a customer' : 'No saved customers'}
            value={customerId}
            onChange={(e) => setCustomerId(e.target.value)}
          />
          <Input
            label="Or Walk-in Name"
            name="so-walkin"
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
            disabled={!!customerId}
            placeholder="Walk-in customer"
          />
        </div>
        <LineItemsEditor lines={lines} onChange={setLines} priceField="sellingPrice" priceLabel="Rate" showNotes />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Input
            label="Payment Terms"
            name="so-terms"
            value={paymentTerms}
            onChange={(e) => setPaymentTerms(e.target.value)}
            placeholder="e.g. Net 30, 50% Advance"
          />
          <Input
            label="Delivery Notes & Reference"
            name="so-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g. Dispatched from main warehouse"
          />
        </div>
        <p className="text-xs text-slate-500 bg-slate-50 p-2.5 rounded-xl border border-slate-200/80">
          💡 Confirming the document commits the sale; inventory is reserved and deducted only when the order is fulfilled.
        </p>
      </div>
    </Modal>
  );
}

export function SalesOpsPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<'orders' | 'returns' | 'credit-notes' | 'shipments' | 'commissions' | 'repeat-orders'>('orders');
  const [showAnalytics, setShowAnalytics] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [actionMenuOpenId, setActionMenuOpenId] = useState<string | null>(null);

  // Queries
  // Scoped to real sales orders. Unfiltered, this endpoint returns every
  // SalesOrderModel document — quotations and proforma included — so the
  // Sales Orders screen was listing documents that are not orders at all.
  // A comma-separated status list arrives from the dashboard's Open Sales
  // tile (e.g. ?status=confirmed,fulfilled,invoiced,partially_paid) so the
  // tile's count and this list's rows can never disagree — see
  // src/shared/dashboard/kpi.js.
  const [statusParam] = useState(() => new URLSearchParams(window.location.search).get('status') || undefined);
  const { data: ordersData, refetch: refetchOrders } = useSalesOrders({ status: statusParam, limit: 500 });
  const { data: returnsData, refetch: refetchReturns } = useSaleReturns();
  const { data: shipmentsData, refetch: refetchShipments } = useShipments();
  const { data: creditNotesData, refetch: refetchCreditNotes } = useCreditNotes();
  const { data: commissionsData, refetch: refetchCommissions } = useCommissionAccruals();
  const { data: repeatOrders = [], refetch: refetchRepeatOrders } = useRepeatOrders();

  const orders = useMemo(() => ordersData?.data || [], [ordersData]);
  const returns = useMemo(() => returnsData?.data || [], [returnsData]);
  const shipments = useMemo(() => shipmentsData?.data || [], [shipmentsData]);
  const creditNotes = useMemo(() => creditNotesData?.data || [], [creditNotesData]);
  const commissions = useMemo(() => commissionsData?.data || [], [commissionsData]);

  // Aggregate Metrics (scoped to confirmed/fulfilled sales orders and direct sales)
  const pipelineValue = useMemo(
    () => orders.filter((o) => o.status !== 'cancelled' && (o.documentType === 'sales_order' || o.documentType === 'direct_sale')).reduce((s, o) => s + (o.grandTotal || 0), 0),
    [orders]
  );
  const activeOrdersCount = useMemo(
    () => orders.filter((o) => ['confirmed', 'fulfilled'].includes(o.status) && (o.documentType === 'sales_order' || o.documentType === 'direct_sale')).length,
    [orders]
  );
  const totalCommission = commissionsData?.totalCommission || 0;
  const inTransitShipments = shipments.filter((s) => s.status === 'shipped').length;

  const handleRefreshAll = () => {
    refetchOrders();
    refetchReturns();
    refetchShipments();
    refetchCreditNotes();
    refetchCommissions();
    refetchRepeatOrders();
    toast.success('Sales operations data refreshed');
  };

  const tabs = [
    { id: 'orders', label: 'Orders & Quotations', count: orders.length, icon: FileText },
    { id: 'shipments', label: 'Shipments & Logistics', count: shipments.length, icon: Truck },
    { id: 'repeat-orders', label: 'Repeat Orders', count: repeatOrders.length, icon: Repeat },
    { id: 'returns', label: 'Sales Returns', count: returns.length, icon: RotateCcw },
    { id: 'credit-notes', label: 'Credit Notes', count: creditNotes.length, icon: CreditCard },
    { id: 'commissions', label: 'Commissions', count: commissions.length, icon: DollarSign },
  ] as const;

  return (
    <div className="flex h-full min-h-screen w-full flex-col gap-5 pb-16">
      <PageHeader
        icon={ClipboardList}
        eyebrow="Sales"
        title="Sales Orders"
        description="Confirmed orders from quotation to fulfilment — shipments, returns, credit notes and commissions."
        actions={<>
          <Button variant="outline" onClick={handleRefreshAll}>
            <RefreshCcw className="h-4 w-4" /> Refresh
          </Button>
          {/* Full builder (billing address, credit limit, dispatch/delivery
              dates, shipping) vs. the quick multi-type modal — both kept. */}
          <Button variant="outline" onClick={() => navigate(ROUTES.salesOrderCreate)} title="Full Sales Order form with billing address, credit limit, dispatch/delivery dates and shipping">
            <ClipboardList className="h-4 w-4" /> Full Sales Order Form
          </Button>
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4" /> New Order / Quotation
          </Button>
        </>}
      />

      <StatGrid cols={5}>
        <StatCard label="Total Documents" value={orders.length} hint="Orders, quotes & proformas" icon={ClipboardList} tone="neutral" />
        <StatCard label="Pipeline Revenue" value={formatINR(pipelineValue)} hint="Excludes cancelled orders" icon={IndianRupee} tone="primary" />
        <StatCard label="In Fulfilment" value={activeOrdersCount} hint="Confirmed or fulfilled" icon={PackageCheck} tone="success" />
        <StatCard label="In Transit" value={inTransitShipments} hint={`of ${shipments.length} shipments`} icon={Truck} tone="info" />
        <StatCard label="Commission" value={formatINR(totalCommission)} hint="Across sales representatives" icon={Percent} tone="warning" />
      </StatGrid>

      {/* ── ANALYTICS CHARTS SECTION ── */}
      {showAnalytics && (
        <AnalyticsPanel storageKey="sales-orders" title="Sales analytics" description="Revenue trend, order funnel, document types, top accounts and products">
          <SalesOpsAnalytics orders={orders} returns={returns} shipments={shipments} />
        </AnalyticsPanel>
      )}

      {/* ── MODULE WORKFLOW GUIDE ── */}
      <ModuleGuide
        summary="A connected sales chain from customer quotation through delivery, invoicing and commission. Each status change updates the next module automatically."
        steps={[
          'Confirm the quotation/order → records the customer commitment.',
          'Fulfill/ship → reduces stock, generates shipment and tracks the delivery.',
          'Invoice → creates accounts receivable; receipt reduces the customer due.',
        ]}
        formulas={[
          { label: 'Order Total', formula: 'Subtotal − Discount + Taxes (GST)' },
          { label: 'Commission Calculation', formula: 'Eligible base amount × Plan rate %' },
        ]}
      />

      {/* ── TABS NAVIGATION BAR ── */}
      <div className="flex items-center justify-between gap-3 border-b border-slate-200 pb-1 overflow-x-auto">
        <div className="flex items-center gap-1.5">
          {tabs.map((t) => {
            const Icon = t.icon;
            const isActive = tab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 rounded-xl px-4 py-2.5 text-xs font-bold transition-all whitespace-nowrap ${
                  isActive
                    ? 'bg-primary text-white shadow-sm '
                    : 'text-slate-600 hover:bg-slate-200/60 hover:text-slate-900'
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                {t.label}
                <span
                  className={`rounded-full px-2 py-0.5 text-data-xs font-bold ${
                    isActive ? 'bg-white/20 text-white' : 'bg-slate-200 text-slate-700'
                  }`}
                >
                  {t.count}
                </span>
              </button>
            );
          })}
        </div>

        <button
          type="button"
          onClick={() => setShowAnalytics(!showAnalytics)}
          className="text-xs font-semibold text-slate-500 hover:text-blue-600 whitespace-nowrap px-2"
        >
          {showAnalytics ? 'Hide Analytics ↑' : 'Show Analytics ↓'}
        </button>
      </div>

      {/* ── TAB CONTENTS ── */}
      {tab === 'orders' && <OrdersTab orders={orders} onOpenCreate={() => setCreateOpen(true)} />}
      {tab === 'shipments' && <ShipmentsTab shipments={shipments} />}
      {tab === 'repeat-orders' && <RepeatOrdersTab profiles={repeatOrders} />}
      {tab === 'returns' && <ReturnsTab returns={returns} />}
      {tab === 'credit-notes' && <CreditNotesTab creditNotes={creditNotes} />}
      {tab === 'commissions' && <CommissionsTab commissions={commissions} totalCommission={totalCommission} />}

      {/* Modal */}
      <NewSalesOrderModal open={createOpen} onClose={() => setCreateOpen(false)} />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* 1. ORDERS TAB                                                              */
/* ─────────────────────────────────────────────────────────────────────────── */
function OrdersTab({ orders, onOpenCreate }: { orders: any[]; onOpenCreate: () => void }) {
  const navigate = useNavigate();
  const actionMut = useSalesOrderAction();
  const convertMut = useConvertSOtoPO();

  const getDocDetailUrl = (doc: any) => {
    if (!doc?._id) return ROUTES.salesOrders;
    const docType = String(doc.documentType || '').toLowerCase();
    if (docType === 'quotation') return `/quotations/${doc._id}`;
    if (docType === 'proforma') return `/proforma/${doc._id}`;
    if (docType === 'direct_sale' || docType === 'invoice') return `/invoices/${doc._id}`;
    return `/sales-orders/${doc._id}`;
  };
  const [searchTerm, setSearchTerm] = useState('');
  const [docTypeFilter, setDocTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('');
  const [productFilter, setProductFilter] = useState('');
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [repeatOrder, setRepeatOrder] = useState<any | null>(null);
  const [repeatFrequency, setRepeatFrequency] = useState('monthly');
  const [repeatInterval, setRepeatInterval] = useState(1);
  const [repeatStartDate, setRepeatStartDate] = useState('');
  const [csatOrder, setCsatOrder] = useState<any | null>(null);
  const [csatScore, setCsatScore] = useState(5);
  const [csatFeedback, setCsatFeedback] = useState('');
  const [upsellOrder, setUpsellOrder] = useState<any | null>(null);
  const [advanceModalOrder, setAdvanceModalOrder] = useState<any | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<{ id: string; top: number; right: number } | null>(null);
  const createOrderMut = useCreateSalesOrder();
  const createRepeatOrderMut = useCreateRepeatOrder();
  const { data: relatedProducts = [], isLoading: relatedLoading } = useRelatedProducts(upsellOrder?._id ?? null);

  const availableProducts = useMemo(() => {
    const map = new Map<string, string>();
    for (const o of orders) {
      for (const line of o.lines || []) {
        const id = line.productId?._id ? String(line.productId._id) : line.productId ? String(line.productId) : line.description;
        const name = line.productId?.name || line.description || line.productCode;
        if (id && name && !map.has(id)) {
          map.set(id, name);
        }
      }
    }
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [orders]);

  const overdueOrdersCount = useMemo(() => {
    return orders.filter((o) => Boolean(o.dispatchProgress?.isOverdue)).length;
  }, [orders]);


  const openRepeat = (o: any) => {
    setRepeatFrequency('monthly');
    setRepeatInterval(1);
    setRepeatStartDate(localToday());
    setRepeatOrder(o);
  };

  const handleRepeat = async () => {
    if (!repeatOrder) return;
    const customerId = repeatOrder.customerId?._id || repeatOrder.customerId;
    if (!customerId) {
      toast.error('This order has no linked customer to repeat — add one before setting up a repeat order.');
      return;
    }
    try {
      await createRepeatOrderMut.mutateAsync({
        name: `Repeat of ${repeatOrder.orderNumber || repeatOrder._id} — ${repeatOrder.customerId?.name || repeatOrder.customerName || 'customer'}`,
        frequency: repeatFrequency,
        interval: repeatInterval,
        startDate: repeatStartDate,
        template: {
          customerId,
          paymentTerms: 'Due on Receipt',
          lineItems: (repeatOrder.lines || []).map((l: any) => ({
            productId: l.productId?._id || l.productId,
            description: l.description,
            quantity: l.quantity,
            rate: l.unitPrice,
            discount: l.discount || 0,
            cgstRate: l.cgstRate || 0,
            sgstRate: l.sgstRate || 0,
            igstRate: l.igstRate || 0,
          })),
        },
      });
      toast.success('Repeat Order set up — see the Repeat Orders tab.');
      setRepeatOrder(null);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Could not set up repeat order.');
    }
  };

  const openCsat = (o: any) => { setCsatScore(5); setCsatFeedback(''); setCsatOrder(o); };

  const handleCsat = async () => {
    if (!csatOrder) return;
    try {
      await actionMut.mutateAsync({ id: csatOrder._id, action: 'csat', body: { score: csatScore, feedback: csatFeedback } });
      toast.success('Customer satisfaction logged.');
      setCsatOrder(null);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Could not log satisfaction.');
    }
  };

  const handleCreateQuotationFromSuggestion = (suggestion: (typeof relatedProducts)[number]) => {
    if (!upsellOrder) return;
    const customerId = upsellOrder.customerId?._id || upsellOrder.customerId;
    createOrderMut.mutate(
      {
        documentType: 'quotation',
        customerId: customerId || undefined,
        customerName: customerId ? undefined : upsellOrder.customerName,
        paymentTerms: 'Due on Receipt',
        notes: `Cross-sell suggestion from ${upsellOrder.orderNumber || upsellOrder._id}`,
        lines: [emptyLineFromProduct(suggestion.product, 'sellingPrice')],
      },
      {
        onSuccess: () => {
          toast.success(`Quotation started for ${suggestion.product.name} — edit it from Orders & Quotations.`);
          setUpsellOrder(null);
        },
        onError: (err: any) => toast.error(err?.response?.data?.error || 'Could not start the quotation.'),
      },
    );
  };

  const statusBadge = (s: string, documentType?: string, o?: any) => {
    if (s === 'draft' && documentType === 'sales_order') {
      return (
        <div className="flex flex-col items-center gap-1">
          <span className="rounded-full px-2.5 py-0.5 text-data-xs font-bold border bg-slate-100 text-slate-700 border-slate-200">
            Order Received
          </span>
        </div>
      );
    }
    const map: Record<string, { label: string; bg: string }> = {
      draft: { label: 'Draft', bg: 'bg-slate-100 text-slate-700 border-slate-200' },
      quotation: { label: 'Quotation', bg: 'bg-purple-50 text-purple-700 border-purple-200' },
      proforma: { label: 'Proforma', bg: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
      confirmed: { label: 'Confirmed', bg: 'bg-blue-50 text-blue-700 border-blue-200' },
      fulfilled: { label: 'Fulfilled', bg: 'bg-teal-50 text-teal-700 border-teal-200' },
      invoiced: { label: 'Invoiced', bg: 'bg-amber-50 text-amber-700 border-amber-200' },
      partially_paid: { label: 'Partial Paid', bg: 'bg-yellow-50 text-yellow-800 border-yellow-200' },
      paid: { label: 'Paid', bg: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
      cancelled: { label: 'Cancelled', bg: 'bg-rose-50 text-rose-700 border-rose-200' },
    };
    const item = map[s] || { label: s.replace('_', ' '), bg: 'bg-slate-100 text-slate-700 border-slate-200' };

    const grand = Number(o?.grandTotal) || 0;
    const paid = Number(o?.amountPaid) || 0;
    const isFullyPaid = (paid >= grand && grand > 0) || o?.paymentStatus === 'paid';
    const isPartiallyPaid = (paid > 0 && paid < grand) || o?.paymentStatus === 'partial';

    return (
      <div className="flex flex-col items-center gap-1">
        <span className={`rounded-full px-2.5 py-0.5 text-data-xs font-bold border ${item.bg}`}>{item.label}</span>
        {isFullyPaid && s !== 'cancelled' && (
          <span className="inline-flex items-center gap-0.5 rounded-full bg-emerald-50 px-2 py-0.5 text-[9px] font-bold text-emerald-700 border border-emerald-200">
            ✓ Paid
          </span>
        )}
        {isPartiallyPaid && s !== 'cancelled' && (
          <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-50 px-2 py-0.5 text-[9px] font-bold text-amber-800 border border-amber-200">
            {formatINR(paid)} ({grand > 0 ? Math.round((paid / grand) * 100) : 0}%)
          </span>
        )}
      </div>
    );
  };

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return orders.filter((o) => {
      const matchesSearch =
        !q ||
        [
          o.orderNumber,
          o.customerId?.name,
          o.customerName,
          o.customerPhone,
          o.customerEmail,
          o.customerPoNumber,
          o.documentType,
          o.notes,
        ].some((f) => (f ?? '').toLowerCase().includes(q)) ||
        (o.lines || []).some((l: any) =>
          [l.description, l.productCode, l.packSize, l.unit]
            .some((f) => (f ?? '').toLowerCase().includes(q))
        );

      const matchesDocType =
        !docTypeFilter || docTypeFilter === 'all' || o.documentType === docTypeFilter;

      const matchesStatus = (() => {
        if (!statusFilter) return true;
        if (statusFilter === 'paid') {
          return (
            o.status === 'paid' ||
            o.paymentStatus === 'paid' ||
            (Number(o.amountPaid) >= Number(o.grandTotal) && Number(o.grandTotal) > 0)
          );
        }
        if (statusFilter === 'partially_paid') {
          return (
            o.status === 'partially_paid' ||
            o.paymentStatus === 'partial' ||
            (Number(o.amountPaid) > 0 && Number(o.amountPaid) < Number(o.grandTotal))
          );
        }
        if (statusFilter === 'unpaid') {
          return (
            (Number(o.amountPaid) === 0 || !o.amountPaid) &&
            o.status !== 'cancelled' &&
            Number(o.grandTotal) > 0
          );
        }
        if (statusFilter === 'quotation') {
          return o.documentType === 'quotation' || o.status === 'quotation';
        }
        if (statusFilter === 'draft') {
          return o.status === 'draft';
        }
        if (statusFilter === 'confirmed') {
          return o.status === 'confirmed';
        }
        if (statusFilter === 'fulfilled') {
          return o.status === 'fulfilled';
        }
        if (statusFilter === 'invoiced') {
          return o.status === 'invoiced';
        }
        if (statusFilter === 'cancelled') {
          return o.status === 'cancelled';
        }
        return o.status === statusFilter;
      })();

      const matchesProduct =
        !productFilter ||
        (o.lines || []).some((l: any) => {
          const pId = l.productId?._id
            ? String(l.productId._id)
            : l.productId
            ? String(l.productId)
            : l.description;
          return (
            pId === productFilter ||
            (l.description &&
              l.description.toLowerCase().includes(productFilter.toLowerCase()))
          );
        });

      const matchesOverdue = !overdueOnly || Boolean(o.dispatchProgress?.isOverdue);

      return (
        matchesSearch &&
        matchesDocType &&
        matchesStatus &&
        matchesProduct &&
        matchesOverdue
      );
    });
  }, [orders, searchTerm, docTypeFilter, statusFilter, productFilter, overdueOnly]);

  const handleConvertToPO = async (id: string) => {
    try {
      const result = await convertMut.mutateAsync(id);
      const poCount = result.purchaseOrders?.length ?? 1;
      toast.success(
        `${poCount} draft PO${poCount === 1 ? '' : 's'} created for ${result.shortfallLines} item(s) with stock shortfall.`
      );
      if (result.unassignedProducts?.length) {
        toast.error(`No preferred vendor set on: ${result.unassignedProducts.join(', ')} — these were skipped.`);
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Could not create PO');
    }
  };

  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white shadow-sm overflow-hidden">
      {/* Table Filter Bar */}
      <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-b border-slate-100 bg-slate-50/50">
        <div className="flex flex-wrap items-center gap-2.5">
          <div className="relative w-56">
            <Search className="absolute left-3 top-2.5 h-3.5 w-3.5 text-slate-400" />
            <input
              type="text"
              placeholder="Search order #, customer, item..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="h-8 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            />
          </div>
          {/* Document Type Filter */}
          <select
            value={docTypeFilter}
            onChange={(e) => setDocTypeFilter(e.target.value)}
            className="h-8 rounded-lg border border-slate-200 bg-white px-2.5 text-xs text-slate-700 focus:outline-none"
            title="Filter by document type"
          >
            <option value="all">All Document Types</option>
            <option value="sales_order">Sales Orders</option>
            <option value="quotation">Quotations</option>
            <option value="proforma">Proforma Invoices</option>
            <option value="direct_sale">Direct Sales</option>
          </select>
          {/* Status & Payment Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-8 rounded-lg border border-slate-200 bg-white px-2.5 text-xs text-slate-700 focus:outline-none"
            title="Filter by status or payment"
          >
            <option value="">All Statuses</option>
            <option value="paid">Paid</option>
            <option value="partially_paid">Partially Paid</option>
            <option value="unpaid">Unpaid</option>
            <option value="confirmed">Confirmed</option>
            <option value="fulfilled">Fulfilled</option>
            <option value="invoiced">Invoiced</option>
            <option value="draft">Draft / Received</option>
            <option value="quotation">Quotation</option>
            <option value="cancelled">Cancelled</option>
          </select>
          {/* Product-wise Filter */}
          <select
            value={productFilter}
            onChange={(e) => setProductFilter(e.target.value)}
            className="h-8 max-w-52 rounded-lg border border-slate-200 bg-white px-2.5 text-xs text-slate-700 focus:outline-none"
            title="Filter by product"
          >
            <option value="">All Products ({availableProducts.length})</option>
            {availableProducts.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          {/* Overdue Dispatches Quick Toggle Chip */}
          <button
            type="button"
            onClick={() => setOverdueOnly(!overdueOnly)}
            className={`inline-flex items-center gap-1.5 h-8 rounded-lg px-2.5 text-xs font-bold transition-colors border ${
              overdueOnly
                ? 'bg-rose-600 text-white border-rose-600 shadow-xs'
                : overdueOrdersCount > 0
                ? 'bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100'
                : 'bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100'
            }`}
            title="Show orders that are overdue for dispatch"
          >
            <span>🚨 Overdue Dispatches</span>
            <span
              className={`rounded-full px-1.5 py-0.5 text-data-xs font-extrabold ${
                overdueOnly ? 'bg-white text-rose-700' : 'bg-rose-200 text-rose-900'
              }`}
            >
              {overdueOrdersCount}
            </span>
          </button>
        </div>

        <button
          type="button"
          onClick={onOpenCreate}
          className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-bold text-white shadow-xs hover:bg-primary-dim transition-colors"
        >
          <Plus className="h-3.5 w-3.5" /> New Order / Quotation
        </button>
      </div>

      {/* Table Body */}
      <div className="overflow-x-auto rounded-xl border border-slate-200/80 bg-white shadow-xs">
        <table className="w-full text-left text-xs">
          <thead className="bg-slate-100 text-[11px] font-bold uppercase tracking-wider text-slate-600 border-b border-slate-200">
            <tr>
              <th className="px-2.5 py-2.5 whitespace-nowrap min-w-[105px]">Order Number</th>
              <th className="px-2 py-2.5 whitespace-nowrap min-w-[75px]">Doc Type</th>
              <th className="px-2 py-2.5 whitespace-nowrap min-w-[75px]">Customer PO</th>
              <th className="px-2 py-2.5 whitespace-nowrap min-w-[75px]">Date</th>
              <th className="px-2.5 py-2.5 whitespace-nowrap min-w-[130px] max-w-[170px]">Customer / Account</th>
              <th className="px-2.5 py-2.5 text-right whitespace-nowrap min-w-[95px]">Grand Total</th>
              <th className="px-2 py-2.5 text-right whitespace-nowrap min-w-[95px]">GST Amount</th>
              <th className="px-2 py-2.5 text-center whitespace-nowrap min-w-[85px]">Status</th>
              <th className="px-2 py-2.5 text-center whitespace-nowrap min-w-[85px]">Dispatch</th>
              <th className="px-2.5 py-2.5 text-right whitespace-nowrap min-w-[160px]">Workflow Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.length === 0 && (
              <tr>
                <td colSpan={10} className="px-4 py-12 text-center text-slate-400">
                  No orders or quotations match the criteria.
                </td>
              </tr>
            )}
            {filtered.map((o) => {
              const detailUrl = getDocDetailUrl(o);
              return (
              <tr
                key={o._id}
                role="button"
                tabIndex={0}
                onClick={() => navigate(detailUrl)}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && navigate(detailUrl)}
                className="cursor-pointer hover:bg-blue-50/40 transition-colors group"
              >
                <td className="px-2.5 py-2.5 font-bold text-slate-900 whitespace-nowrap min-w-[105px]">
                  <a
                    href={detailUrl}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(detailUrl);
                    }}
                    className="inline-flex items-center gap-1 text-primary hover:text-primary-dim hover:underline font-bold"
                    title={`Open full ${o.documentType?.replace('_', ' ') || 'document'}`}
                  >
                    {o.orderNumber || 'DRAFT'}
                    <Eye className="h-3 w-3 opacity-0 group-hover:opacity-100 text-primary transition-opacity" />
                  </a>
                </td>
                <td className="px-2 py-2.5 whitespace-nowrap min-w-[75px]">
                  <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600 capitalize">
                    {o.documentType?.replace('_', ' ')}
                  </span>
                </td>
                <td className="px-2 py-2.5 text-slate-600 whitespace-nowrap min-w-[75px]">
                  {o.customerPoNumber ? (
                    <div className="flex flex-col">
                      <span className="font-semibold text-slate-800 text-xs">{o.customerPoNumber}</span>
                      {o.customerPoDate && (
                        <span className="text-[10px] text-slate-400">
                          {new Date(o.customerPoDate).toLocaleDateString('en-IN')}
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="text-slate-300">—</span>
                  )}
                </td>
                <td className="px-2 py-2.5 text-slate-500 font-medium whitespace-nowrap min-w-[75px]">
                  {new Date(o.date).toLocaleDateString('en-IN')}
                </td>
                <td className="px-2.5 py-2.5 font-semibold text-slate-800 whitespace-nowrap min-w-[130px] max-w-[170px] truncate" title={o.customerId?.name || o.customerName || ''}>
                  {o.customerId?.name || o.customerName || '—'}
                </td>
                <td className="px-2.5 py-2.5 text-right font-bold tabular-nums text-slate-900 whitespace-nowrap min-w-[95px]">
                  <div>{formatDocTotal(o as never)}</div>
                  {docInrEquivalent(o as never) && <div className="text-[10px] font-medium text-slate-400">≈ {docInrEquivalent(o as never)}</div>}
                  {(() => {
                    const lines = o.lines || [];
                    const split = lines.reduce(
                      (sum: { taxable: number }, line: any) => {
                        const qty = Number(line.quantity) || 0;
                        const unitPrice = Number(line.unitPrice) || 0;
                        const gross = qty * unitPrice;
                        let disc: number;
                        if (line.discountType === 'fixed' || line.discountType === 'amount') {
                          disc = Number(line.discount) || 0;
                        } else {
                          disc = gross * ((Number(line.discount) || 0) / 100);
                        }
                        sum.taxable += Math.max(0, gross - disc);
                        return sum;
                      },
                      { taxable: 0 }
                    );
                    const tax = Number(o.taxTotal) || 0;
                    const taxable = split.taxable > 0 ? split.taxable : Math.max(0, (o.grandTotal || 0) - tax);
                    return taxable > 0 && Math.abs((o.grandTotal || 0) - taxable) > 1 ? (
                      <div className="text-[10px] font-normal text-slate-400">
                        Taxable: {formatINR(taxable)}
                      </div>
                    ) : null;
                  })()}
                </td>
                <td className="px-2 py-2.5 text-right tabular-nums whitespace-nowrap min-w-[95px]">
                  {(() => {
                    const lines = o.lines || [];
                    const split = lines.reduce(
                      (sum: { cgst: number; sgst: number; igst: number; taxable: number }, line: any) => {
                        const qty = Number(line.quantity) || 0;
                        const unitPrice = Number(line.unitPrice) || 0;
                        const gross = qty * unitPrice;
                        let disc: number;
                        if (line.discountType === 'fixed' || line.discountType === 'amount') {
                          disc = Number(line.discount) || 0;
                        } else {
                          disc = gross * ((Number(line.discount) || 0) / 100);
                        }
                        const taxable = Math.max(0, gross - disc);
                        sum.taxable += taxable;
                        sum.cgst += (taxable * (Number(line.cgstRate) || 0)) / 100;
                        sum.sgst += (taxable * (Number(line.sgstRate) || 0)) / 100;
                        sum.igst += (taxable * (Number(line.igstRate) || 0)) / 100;
                        return sum;
                      },
                      { cgst: 0, sgst: 0, igst: 0, taxable: 0 }
                    );

                    const calculatedTax = split.cgst + split.sgst + split.igst;
                    const recordedTax = Number(o.taxTotal) || 0;
                    const totalTax = recordedTax > 0 ? recordedTax : calculatedTax;

                    let cgst = split.cgst;
                    let sgst = split.sgst;
                    let igst = split.igst;

                    if (totalTax > 0 && calculatedTax > 0 && Math.abs(totalTax - calculatedTax) > 0.05) {
                      const ratio = totalTax / calculatedTax;
                      cgst *= ratio;
                      sgst *= ratio;
                      igst *= ratio;
                    } else if (totalTax > 0 && calculatedTax === 0) {
                      cgst = totalTax / 2;
                      sgst = totalTax / 2;
                    }

                    return totalTax > 0 ? (
                      <div className="text-data-xs leading-4">
                        <div className="font-bold text-slate-900">{formatINR(totalTax)}</div>
                        <div className="text-[10px] text-slate-400">
                          {igst > 0
                            ? `IGST ${formatINR(igst)}`
                            : `CGST ${formatINR(cgst)} · SGST ${formatINR(sgst)}`}
                        </div>
                      </div>
                    ) : (
                      <span className="text-slate-300">—</span>
                    );
                  })()}
                </td>
                <td className="px-2 py-2.5 text-center whitespace-nowrap min-w-[85px]">
                  <div className="flex flex-col items-center gap-0.5">
                    {statusBadge(o.status, o.documentType, o)}
                    {o.csatScore && (
                      <span
                        className="inline-flex items-center gap-0.5 rounded-full bg-amber-50 px-1.5 py-0.5 text-[9px] font-bold text-amber-700 border border-amber-200 mt-0.5"
                        title={o.csatFeedback || ''}
                      >
                        <Star className="h-2.5 w-2.5 fill-amber-500 text-amber-500" /> {o.csatScore}/5
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-2 py-2.5 text-center whitespace-nowrap min-w-[85px]">
                  {o.dispatchProgress?.status === 'delivered' ? (
                    <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-data-xs font-semibold text-emerald-700 border border-emerald-200">
                      Delivered
                    </span>
                  ) : o.dispatchProgress?.status === 'dispatched' ? (
                    <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-data-xs font-semibold text-blue-700 border border-blue-200">
                      Dispatched
                    </span>
                  ) : o.dispatchProgress?.status === 'partially_dispatched' ? (
                    <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-data-xs font-semibold text-amber-800 border border-amber-200">
                      Partly Disp.
                    </span>
                  ) : (
                    <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-data-xs font-medium text-slate-500">
                      Pending
                    </span>
                  )}
                </td>
                <td className="px-2.5 py-2.5 text-right whitespace-nowrap min-w-[160px] transition-colors">
                  <div className="flex items-center justify-end gap-1.5 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      onClick={() => navigate(detailUrl)}
                      className="inline-flex items-center gap-1 rounded-lg bg-slate-100 px-2 py-1 text-data-xs font-semibold text-slate-700 border border-slate-200 hover:bg-slate-200 transition-colors"
                      title="View Details"
                    >
                      <Eye className="h-3 w-3" /> View
                    </button>

                    {['draft', 'quotation', 'proforma'].includes(o.status) && (
                      <button
                        type="button"
                        onClick={() => {
                          actionMut.mutateAsync({ id: o._id, action: 'confirm' });
                          toast.success('Order confirmed.');
                        }}
                        className="rounded-lg bg-blue-50 px-2 py-1 text-data-xs font-bold text-blue-700 border border-blue-200 hover:bg-blue-100 transition-colors"
                      >
                        Confirm
                      </button>
                    )}

                    {o.status === 'confirmed' && (
                      <button
                        type="button"
                        onClick={() => {
                          actionMut.mutateAsync({ id: o._id, action: 'fulfill' });
                          toast.success('Fulfilled — stock deducted.');
                        }}
                        className="rounded-lg bg-emerald-50 px-2 py-1 text-data-xs font-bold text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition-colors"
                      >
                        Fulfill
                      </button>
                    )}

                    {['confirmed', 'fulfilled'].includes(o.status) && (
                      <DocumentActions docType={o.documentType} docId={o._id} size="sm" />
                    )}

                    {/* Compact Dropdown Menu trigger */}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (menuAnchor?.id === o._id) {
                          setMenuAnchor(null);
                        } else {
                          const rect = e.currentTarget.getBoundingClientRect();
                          setMenuAnchor({
                            id: o._id,
                            top: rect.bottom + 4,
                            right: window.innerWidth - rect.right,
                          });
                        }
                      }}
                      className="inline-flex items-center justify-center h-6 w-6 rounded-lg border border-slate-200 bg-white text-slate-500 hover:bg-slate-100 hover:text-slate-800 transition-colors"
                      title="More actions"
                    >
                      <MoreVertical className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
          </tbody>
        </table>
      </div>

      {/* Render Dropdown Menu via Portal to prevent any container clipping */}
      {menuAnchor && createPortal(
        (() => {
          const activeOrder = orders.find((x) => x._id === menuAnchor.id);
          if (!activeOrder) return null;
          const detailUrl = getDocDetailUrl(activeOrder);
          return (
            <>
              <div
                className="fixed inset-0 z-[9998]"
                onClick={(e) => {
                  e.stopPropagation();
                  setMenuAnchor(null);
                }}
              />
              <div
                style={{
                  position: 'fixed',
                  top: `${menuAnchor.top}px`,
                  right: `${menuAnchor.right}px`,
                }}
                className="w-48 rounded-xl border border-slate-200 bg-white p-1 text-left shadow-2xl z-[9999] animate-in fade-in zoom-in-95 duration-100"
                onClick={(e) => e.stopPropagation()}
              >
                {!['cancelled', 'superseded'].includes(activeOrder.status) && (
                  <button
                    type="button"
                    onClick={() => {
                      setMenuAnchor(null);
                      setAdvanceModalOrder(activeOrder);
                    }}
                    className="flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors"
                  >
                    <Wallet className="h-3.5 w-3.5 text-emerald-600" />
                    Record Advance
                  </button>
                )}

                {activeOrder.status === 'confirmed' && (
                  <button
                    type="button"
                    onClick={() => {
                      setMenuAnchor(null);
                      handleConvertToPO(activeOrder._id);
                    }}
                    className="flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-indigo-50 hover:text-indigo-700 transition-colors"
                  >
                    <FileText className="h-3.5 w-3.5 text-indigo-600" />
                    Convert (SO→PO)
                  </button>
                )}

                {['fulfilled', 'invoiced', 'partially_paid', 'paid'].includes(activeOrder.status) && (
                  <button
                    type="button"
                    onClick={() => {
                      setMenuAnchor(null);
                      openRepeat(activeOrder);
                    }}
                    className="flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-violet-50 hover:text-violet-700 transition-colors"
                  >
                    <Repeat className="h-3.5 w-3.5 text-violet-600" />
                    Repeat Order
                  </button>
                )}

                {['fulfilled', 'invoiced', 'partially_paid', 'paid'].includes(activeOrder.status) && !activeOrder.csatScore && (
                  <button
                    type="button"
                    onClick={() => {
                      setMenuAnchor(null);
                      openCsat(activeOrder);
                    }}
                    className="flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-amber-50 hover:text-amber-700 transition-colors"
                  >
                    <Star className="h-3.5 w-3.5 text-amber-500" />
                    Log Satisfaction
                  </button>
                )}

                {['fulfilled', 'invoiced', 'partially_paid', 'paid'].includes(activeOrder.status) && (
                  <button
                    type="button"
                    onClick={() => {
                      setMenuAnchor(null);
                      setUpsellOrder(activeOrder);
                    }}
                    className="flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-cyan-50 hover:text-cyan-700 transition-colors"
                  >
                    <TrendingUp className="h-3.5 w-3.5 text-cyan-600" />
                    Suggest Upsell
                  </button>
                )}

                <div className="my-1 border-t border-slate-100" />

                <button
                  type="button"
                  onClick={() => {
                    setMenuAnchor(null);
                    navigate(detailUrl);
                  }}
                  className="flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100 transition-colors"
                >
                  <Eye className="h-3.5 w-3.5 text-slate-400" />
                  Open Document
                </button>
              </div>
            </>
          );
        })(),
        document.body
      )}

      <Modal
        open={!!repeatOrder}
        onClose={() => setRepeatOrder(null)}
        title={repeatOrder ? `Repeat ${repeatOrder.orderNumber || repeatOrder._id}` : ''}
        size="sm"
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setRepeatOrder(null)}>Cancel</Button>
            <Button size="sm" disabled={createRepeatOrderMut.isPending} onClick={handleRepeat}>
              Set Up Repeat Order
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-xs text-slate-500">
            Automatically raises a fresh quotation with the same {repeatOrder?.lines?.length || 0} line item(s) and
            customer, on the schedule below. Manage it from the Repeat Orders tab.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Every</label>
              <Input type="number" min={1} max={365} value={repeatInterval} onChange={(e) => setRepeatInterval(Number(e.target.value) || 1)} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Frequency</label>
              <select
                value={repeatFrequency}
                onChange={(e) => setRepeatFrequency(e.target.value)}
                className="h-9 w-full rounded-lg border border-slate-200 bg-white px-2.5 text-sm text-slate-700 focus:outline-none"
              >
                <option value="daily">Day(s)</option>
                <option value="weekly">Week(s)</option>
                <option value="monthly">Month(s)</option>
                <option value="quarterly">Quarter(s)</option>
                <option value="yearly">Year(s)</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Starting</label>
            <Input type="date" value={repeatStartDate} onChange={(e) => setRepeatStartDate(e.target.value)} />
          </div>
        </div>
      </Modal>

      <Modal
        open={!!csatOrder}
        onClose={() => setCsatOrder(null)}
        title={csatOrder ? `Log Satisfaction — ${csatOrder.orderNumber || csatOrder._id}` : ''}
        size="sm"
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setCsatOrder(null)}>Cancel</Button>
            <Button size="sm" disabled={actionMut.isPending} onClick={handleCsat}>
              Save
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-xs text-slate-500">
            How was {csatOrder?.customerId?.name || csatOrder?.customerName || 'the customer'}'s experience with this order?
          </p>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Score</label>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setCsatScore(n)}
                  className="p-1"
                  aria-label={`${n} out of 5`}
                >
                  <Star className={`h-6 w-6 ${n <= csatScore ? 'fill-amber-500 text-amber-500' : 'text-slate-200'}`} />
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Feedback</label>
            <textarea
              value={csatFeedback}
              onChange={(e) => setCsatFeedback(e.target.value)}
              placeholder="What did they say? (optional)"
              rows={3}
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            />
          </div>
        </div>
      </Modal>

      <Modal
        open={!!upsellOrder}
        onClose={() => setUpsellOrder(null)}
        title={upsellOrder ? `Cross-sell — ${upsellOrder.orderNumber || upsellOrder._id}` : ''}
        size="md"
        footer={<Button variant="ghost" size="sm" onClick={() => setUpsellOrder(null)}>Close</Button>}
      >
        <div className="space-y-3">
          <p className="text-xs text-slate-500">
            Based on what other customers bought alongside the same products, computed from real order history.
          </p>
          {relatedLoading && <p className="text-xs text-slate-400 py-6 text-center">Looking up suggestions…</p>}
          {!relatedLoading && relatedProducts.length === 0 && (
            <p className="text-xs text-slate-400 py-6 text-center">
              Not enough order history yet to suggest cross-sells for this order's products.
            </p>
          )}
          <div className="space-y-2">
            {relatedProducts.map((r) => (
              <div key={r.productId} className="flex items-center justify-between rounded-xl border border-slate-200 p-3">
                <div>
                  <p className="text-sm font-bold text-slate-800">{r.product.name}</p>
                  <p className="text-data-xs text-slate-500">
                    {r.product.sku ? `${r.product.sku} · ` : ''}Bought together {r.coOccurrences} time{r.coOccurrences === 1 ? '' : 's'} · ~{formatINR(r.avgUnitPrice)}
                  </p>
                </div>
                <Button size="sm" disabled={createOrderMut.isPending} onClick={() => handleCreateQuotationFromSuggestion(r)}>
                  Create Quotation
                </Button>
              </div>
            ))}
          </div>
        </div>
      </Modal>

      {advanceModalOrder && (
        <RecordAdvanceModal
          open={!!advanceModalOrder}
          onClose={() => setAdvanceModalOrder(null)}
          orderId={advanceModalOrder._id}
          orderNumber={advanceModalOrder.orderNumber}
          documentType={advanceModalOrder.documentType}
          clientName={advanceModalOrder.customerId?.name || advanceModalOrder.customerName}
          grandTotal={advanceModalOrder.grandTotal}
          currentAmountPaid={advanceModalOrder.amountPaid || 0}
          onSuccess={() => setAdvanceModalOrder(null)}
        />
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* 2. SHIPMENTS TAB                                                           */
/* ─────────────────────────────────────────────────────────────────────────── */
function ShipmentsTab({ shipments }: { shipments: any[] }) {
  const actionMut = useShipmentAction();
  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white shadow-sm overflow-hidden">
      <div className="p-4 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-bold text-slate-900">Shipments & Logistics Tracking</h3>
          <p className="text-xs text-slate-500">Track couriers, airway bills, dispatch and delivery states</p>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-xs">
          <thead className="bg-slate-50/80 text-data-xs font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100">
            <tr>
              <th className="px-4 py-3">Shipment #</th>
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3">Order Ref</th>
              <th className="px-4 py-3">Carrier / Tracking</th>
              <th className="px-4 py-3 text-center">Status</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {shipments.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center text-slate-400">
                  No shipments logged yet.
                </td>
              </tr>
            )}
            {shipments.map((s) => (
              <tr key={s._id} className="hover:bg-slate-50/80 transition-colors">
                <td className="px-4 py-3.5 font-bold text-slate-900">{s.shipmentNumber}</td>
                <td className="px-4 py-3.5 text-slate-500 font-medium">{new Date(s.date).toLocaleDateString('en-IN')}</td>
                <td className="px-4 py-3.5 font-semibold text-slate-700">{s.salesOrderId?.orderNumber || '—'}</td>
                <td className="px-4 py-3.5 font-mono text-data-xs text-slate-700">
                  {s.trackingNumber ? (
                    <span className="rounded-md bg-slate-100 px-2 py-0.5 border border-slate-200">
                      {s.carrier || 'Courier'}: {s.trackingNumber}
                    </span>
                  ) : (
                    '—'
                  )}
                </td>
                <td className="px-4 py-3.5 text-center">
                  <span
                    className={`rounded-full px-2.5 py-0.5 text-data-xs font-bold capitalize border ${
                      s.status === 'delivered'
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                        : s.status === 'shipped'
                        ? 'bg-blue-50 text-blue-700 border-blue-200'
                        : 'bg-slate-100 text-slate-700 border-slate-200'
                    }`}
                  >
                    {s.status}
                  </span>
                </td>
                <td className="px-4 py-3.5 text-right">
                  <div className="flex items-center justify-end gap-1.5">
                    {s.status === 'pending' && (
                      <button
                        type="button"
                        onClick={() => {
                          actionMut.mutateAsync({ id: s._id, action: 'ship' });
                          toast.success('Marked as shipped.');
                        }}
                        className="rounded-lg bg-blue-50 px-2.5 py-1 text-data-xs font-bold text-blue-700 border border-blue-200 hover:bg-blue-100 transition-colors"
                      >
                        Ship
                      </button>
                    )}
                    {s.status === 'shipped' && (
                      <button
                        type="button"
                        onClick={() => {
                          actionMut.mutateAsync({ id: s._id, action: 'deliver' });
                          toast.success('Marked as delivered.');
                        }}
                        className="rounded-lg bg-emerald-50 px-2.5 py-1 text-data-xs font-bold text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition-colors"
                      >
                        Deliver
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* 2b. REPEAT ORDERS TAB                                                      */
/* ─────────────────────────────────────────────────────────────────────────── */
function RepeatOrdersTab({ profiles }: { profiles: any[] }) {
  const actionMut = useRepeatOrderAction();
  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white shadow-sm overflow-hidden">
      <div className="p-4 border-b border-slate-100 bg-slate-50/50">
        <h3 className="text-sm font-bold text-slate-900">Repeat Orders</h3>
        <p className="text-xs text-slate-500">
          Set up from a fulfilled order's "Repeat Order" button. Each run raises a fresh quotation automatically.
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-xs">
          <thead className="bg-slate-50/80 text-data-xs font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100">
            <tr>
              <th className="px-4 py-3">Profile</th>
              <th className="px-4 py-3">Schedule</th>
              <th className="px-4 py-3">Next run</th>
              <th className="px-4 py-3">Runs</th>
              <th className="px-4 py-3 text-center">Status</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {profiles.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center text-slate-400">
                  No repeat orders set up yet.
                </td>
              </tr>
            )}
            {profiles.map((p) => (
              <tr key={p._id} className="hover:bg-slate-50/80 transition-colors">
                <td className="px-4 py-3.5 font-semibold text-slate-800">{p.name}</td>
                <td className="px-4 py-3.5 text-slate-600 capitalize">Every {p.interval > 1 ? p.interval : ''} {p.frequency}</td>
                <td className="px-4 py-3.5 text-slate-500">{new Date(p.nextRunAt).toLocaleDateString('en-IN')}</td>
                <td className="px-4 py-3.5 text-slate-500">{p.runCount}</td>
                <td className="px-4 py-3.5 text-center">
                  <span
                    className={`rounded-full px-2.5 py-0.5 text-data-xs font-bold capitalize border ${
                      p.status === 'active'
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                        : p.status === 'failed'
                        ? 'bg-rose-50 text-rose-700 border-rose-200'
                        : 'bg-slate-100 text-slate-700 border-slate-200'
                    }`}
                  >
                    {p.status}
                  </span>
                  {p.lastFailure && <p className="mt-1 max-w-[200px] truncate text-data-xs text-rose-500" title={p.lastFailure}>{p.lastFailure}</p>}
                </td>
                <td className="px-4 py-3.5 text-right">
                  {p.status === 'active' && (
                    <button type="button" onClick={() => actionMut.mutate({ id: p._id, action: 'pause' })} className="rounded-lg bg-slate-50 px-2.5 py-1 text-data-xs font-bold text-slate-700 border border-slate-200 hover:bg-slate-100 transition-colors">
                      Pause
                    </button>
                  )}
                  {p.status === 'paused' && (
                    <button type="button" onClick={() => actionMut.mutate({ id: p._id, action: 'resume' })} className="rounded-lg bg-emerald-50 px-2.5 py-1 text-data-xs font-bold text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition-colors">
                      Resume
                    </button>
                  )}
                  {p.status === 'failed' && (
                    <button type="button" onClick={() => actionMut.mutate({ id: p._id, action: 'retry' })} className="rounded-lg bg-amber-50 px-2.5 py-1 text-data-xs font-bold text-amber-700 border border-amber-200 hover:bg-amber-100 transition-colors">
                      Retry
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* 3. RETURNS TAB                                                             */
/* ─────────────────────────────────────────────────────────────────────────── */
function ReturnsTab({ returns }: { returns: any[] }) {
  const actionMut = useReturnAction();
  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white shadow-sm overflow-hidden">
      <div className="p-4 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-bold text-slate-900">Sales Returns & Restocking</h3>
          <p className="text-xs text-slate-500">Approve returns and restore inventory stock</p>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-xs">
          <thead className="bg-slate-50/80 text-data-xs font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100">
            <tr>
              <th className="px-4 py-3">Return #</th>
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3">Order Ref</th>
              <th className="px-4 py-3 text-right">Quantity</th>
              <th className="px-4 py-3 text-right">Refund Amount</th>
              <th className="px-4 py-3 text-center">Status</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {returns.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center text-slate-400">
                  No sales returns logged.
                </td>
              </tr>
            )}
            {returns.map((r) => (
              <tr key={r._id} className="hover:bg-slate-50/80 transition-colors">
                <td className="px-4 py-3.5 font-bold text-slate-900">{r.returnNumber || 'DRAFT'}</td>
                <td className="px-4 py-3.5 text-slate-500 font-medium">{new Date(r.date).toLocaleDateString('en-IN')}</td>
                <td className="px-4 py-3.5 font-semibold text-slate-700">{r.salesOrderId?.orderNumber || '—'}</td>
                <td className="px-4 py-3.5 text-right font-bold text-slate-800">{r.totalReturned}</td>
                <td className="px-4 py-3.5 text-right font-bold tabular-nums text-rose-600">{formatINR(r.refundAmount || 0)}</td>
                <td className="px-4 py-3.5 text-center">
                  <span
                    className={`rounded-full px-2.5 py-0.5 text-data-xs font-bold capitalize border ${
                      r.status === 'posted'
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                        : r.status === 'approved'
                        ? 'bg-blue-50 text-blue-700 border-blue-200'
                        : 'bg-slate-100 text-slate-700 border-slate-200'
                    }`}
                  >
                    {r.status}
                  </span>
                </td>
                <td className="px-4 py-3.5 text-right">
                  <div className="flex items-center justify-end gap-1.5">
                    {r.status === 'draft' && (
                      <button
                        type="button"
                        onClick={() => {
                          actionMut.mutateAsync({ id: r._id, action: 'approve' });
                          toast.success('Approved.');
                        }}
                        className="rounded-lg bg-blue-50 px-2.5 py-1 text-data-xs font-bold text-blue-700 border border-blue-200 hover:bg-blue-100 transition-colors"
                      >
                        Approve
                      </button>
                    )}
                    {r.status === 'approved' && (
                      <button
                        type="button"
                        onClick={() => {
                          actionMut.mutateAsync({ id: r._id, action: 'post' });
                          toast.success('Posted — stock restored.');
                        }}
                        className="rounded-lg bg-emerald-50 px-2.5 py-1 text-data-xs font-bold text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition-colors"
                      >
                        Post (Restock)
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* 4. CREDIT NOTES TAB                                                        */
/* ─────────────────────────────────────────────────────────────────────────── */
function CreditNotesTab({ creditNotes }: { creditNotes: any[] }) {
  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white shadow-sm overflow-hidden">
      <div className="p-4 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-bold text-slate-900">Credit Notes ({creditNotes.length})</h3>
          <p className="text-xs text-slate-500">Auto-generated adjustments from posted sales returns</p>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-xs">
          <thead className="bg-slate-50/80 text-data-xs font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100">
            <tr>
              <th className="px-4 py-3">Credit Note #</th>
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3">Customer</th>
              <th className="px-4 py-3">Reason</th>
              <th className="px-4 py-3 text-right">Amount</th>
              <th className="px-4 py-3 text-center">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {creditNotes.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center text-slate-400">
                  No credit notes generated yet.
                </td>
              </tr>
            )}
            {creditNotes.map((cn) => (
              <tr key={cn._id} className="hover:bg-slate-50/80 transition-colors">
                <td className="px-4 py-3.5 font-bold text-slate-900">{cn.noteNumber}</td>
                <td className="px-4 py-3.5 text-slate-500 font-medium">{new Date(cn.date).toLocaleDateString('en-IN')}</td>
                <td className="px-4 py-3.5 font-semibold text-slate-800">{cn.customerId?.name || '—'}</td>
                <td className="px-4 py-3.5 text-slate-600 truncate max-w-[200px]">{cn.reason || 'Sales return credit'}</td>
                <td className="px-4 py-3.5 text-right font-bold tabular-nums text-slate-900">{formatINR(cn.grandTotal || 0)}</td>
                <td className="px-4 py-3.5 text-center">
                  <span
                    className={`rounded-full px-2.5 py-0.5 text-data-xs font-bold capitalize border ${
                      cn.status === 'posted'
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                        : 'bg-slate-100 text-slate-700 border-slate-200'
                    }`}
                  >
                    {cn.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* 5. COMMISSIONS TAB                                                         */
/* ─────────────────────────────────────────────────────────────────────────── */
function CommissionsTab({ commissions, totalCommission }: { commissions: any[]; totalCommission: number }) {
  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white shadow-sm overflow-hidden">
      <div className="p-4 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-bold text-slate-900">
            Sales Representative Commissions{' '}
            <span className="text-emerald-700">(Total: {formatINR(totalCommission)})</span>
          </h3>
          <p className="text-xs text-slate-500">Accrued agent performance earnings calculated per plan</p>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-xs">
          <thead className="bg-slate-50/80 text-data-xs font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100">
            <tr>
              <th className="px-4 py-3">Agent / Representative</th>
              <th className="px-4 py-3">Order Number</th>
              <th className="px-4 py-3 text-right">Base Amount</th>
              <th className="px-4 py-3 text-right">Commission Earned</th>
              <th className="px-4 py-3 text-center">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {commissions.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-12 text-center text-slate-400">
                  No commissions accrued yet.
                </td>
              </tr>
            )}
            {commissions.map((a: any) => (
              <tr key={a._id} className="hover:bg-slate-50/80 transition-colors">
                <td className="px-4 py-3.5 font-bold text-slate-900">{a.agentId?.display_name || 'Agent'}</td>
                <td className="px-4 py-3.5 font-semibold text-slate-700">{a.salesOrderId?.orderNumber || '—'}</td>
                <td className="px-4 py-3.5 text-right font-medium text-slate-600">{formatINR(a.baseAmount || 0)}</td>
                <td className="px-4 py-3.5 text-right font-bold tabular-nums text-emerald-600">
                  {formatINR(a.commissionAmount || 0)}
                </td>
                <td className="px-4 py-3.5 text-center">
                  <span
                    className={`rounded-full px-2.5 py-0.5 text-data-xs font-bold capitalize border ${
                      a.status === 'paid'
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                        : 'bg-amber-50 text-amber-700 border-amber-200'
                    }`}
                  >
                    {a.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
